import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { Box, Button, Stack } from '@mui/material';

import {
  StyledCard,
  StyledCardContent,
  StyledTypographySubTitle,
  StyledTypographyTitle
} from '../../styled-components';
import { Site, SiteFormFields, SiteFormFieldTitles, SiteInputsForm } from 'constants/sites.constants';
import { pagesEnum } from 'constants/ui.constants';
import { LocationIcon } from 'assets/icons/Location.icon';
import { SwiftIcon } from 'assets/icons/Swift.icon';
import { CityIcon } from 'assets/icons/City.icon';
import { PermissionIcon } from 'assets/icons/Permission.icon';
import CustomInputForm from 'components/InputsForm/CustomInput';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { createSite, setError } from 'state/reducers/site.reducers';
import { convertKeysToSnakeCase, formResolver } from '../../../utils/global.util';
import _ from 'lodash';

interface IFormInputs {
  dataForm: SiteInputsForm;
  openProfileModal: boolean;
  setOpenProfileModal: Dispatch<SetStateAction<boolean>>;
}

const NewSite: React.FC = () => {
  const navigate = useNavigate();

  const methods = useForm<IFormInputs>({
    defaultValues: {
      dataForm: {}
    },
    mode: 'onTouched',
    resolver: (values) =>
      formResolver(values, [
        { key: SiteFormFields.NAME, displayName: SiteFormFieldTitles.NAME },
        {
          key: SiteFormFields.SITE_ID,
          displayName: SiteFormFieldTitles.SITE_ID
        },
        {
          key: SiteFormFields.COUNTRY,
          displayName: SiteFormFieldTitles.COUNTRY
        },
        { key: SiteFormFields.CITY, displayName: SiteFormFieldTitles.CITY }
      ])
  });
  const {
    formState: { errors }
  } = methods;

  const dispatch = useAppDispatch();
  const auth = useAppSelector((state) => state.auth);
  const { error, sites, loading } = useAppSelector((state) => state.sites);

  const formSubmitHandler: SubmitHandler<IFormInputs> = (newSiteData: IFormInputs) => {
    const parsedNewSiteData = {
      ...convertKeysToSnakeCase(newSiteData.dataForm)
    };
    const createdByFirstName = auth?.userDetails?.firstName;
    const createdByLastName = auth?.userDetails?.lastName;

    dispatch(
      createSite({
        site: parsedNewSiteData,
        extra: { createdByFirstName, createdByLastName }
      })
    );
    navigate(`/${pagesEnum.SITES}`);
  };

  const validateField = (event: any) => {
    const { id, value }: { id: keyof Site; value: string } = event.target;

    if (value?.length) {
      if (['name', 'siteId'].includes(id) && value) {
        const exist = !!sites?.find((site: Site) => {
          return site[id] === value;
        });
        if (exist) {
          dispatch(
            setError({
              key: id,
              error: true
            })
          );
          return;
        }
        dispatch(
          setError({
            key: id,
            error: false
          })
        );
      }
    }
  };

  useEffect(() => {
    return () => {
      dispatch(setError(null));
    };
  }, [dispatch]);

  const isSubmitDisabled = useMemo(() => {
    return !_.isEmpty(error) || loading;
  }, [error, loading]);

  const getFieldError = (fieldName: SiteFormFields, message?: string) => {
    if (errors.dataForm?.[fieldName]?.message) {
      return errors.dataForm[fieldName]?.message;
    }

    if (_.has(error, fieldName) && message) {
      return message;
    }

    return '';
  };

  return (
    <Box sx={{ width: '100%' }}>
      <FormProvider {...methods}>
        <form noValidate onChange={validateField} onSubmit={methods.handleSubmit(formSubmitHandler)}>
          <StyledCard sx={{ background: 'transparent' }}>
            <StyledCardContent>
              <Stack spacing={3}>
                <StyledTypographyTitle sx={{ justifyContent: 'flex-start' }}>Add New Site</StyledTypographyTitle>
                <StyledTypographySubTitle sx={{ justifyContent: 'flex-start' }}>
                  Site Information
                </StyledTypographySubTitle>
                <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap>
                  <CustomInputForm
                    disabled={false}
                    defaultValue={''}
                    controllerName={'dataForm.name'}
                    inputRegister={'dataForm.name'}
                    inputId={SiteFormFields.NAME}
                    label={SiteFormFieldTitles.NAME}
                    type={'text'}
                    icon={<LocationIcon />}
                    errorMessage={getFieldError(SiteFormFields.NAME, 'Same site name, Try other name.')}
                  />

                  <CustomInputForm
                    disabled={false}
                    defaultValue={''}
                    controllerName={'dataForm.country'}
                    inputRegister={'dataForm.country'}
                    inputId={SiteFormFields.COUNTRY}
                    label={SiteFormFieldTitles.COUNTRY}
                    type={'text'}
                    icon={<SwiftIcon />}
                    errorMessage={getFieldError(SiteFormFields.COUNTRY)}
                  />

                  <CustomInputForm
                    disabled={false}
                    defaultValue={''}
                    controllerName={'dataForm.city'}
                    inputRegister={'dataForm.city'}
                    inputId={SiteFormFields.CITY}
                    label={SiteFormFieldTitles.CITY}
                    type={'text'}
                    icon={<CityIcon />}
                    errorMessage={getFieldError(SiteFormFields.CITY)}
                  />
                  <CustomInputForm
                    disabled={false}
                    defaultValue={''}
                    controllerName={'dataForm.siteId'}
                    inputRegister={'dataForm.siteId'}
                    inputId={SiteFormFields.SITE_ID}
                    label={SiteFormFieldTitles.SITE_ID}
                    type={'text'}
                    icon={<PermissionIcon />}
                    errorMessage={getFieldError(SiteFormFields.SITE_ID, 'Same site ID, Try other ID.')}
                  />
                </Stack>
              </Stack>
              <Stack spacing={2} direction='row' sx={{ display: 'flex', right: 20, justifyContent: 'flex-end', m: 10 }}>
                <Button
                  color='secondary'
                  data-testid='cancel-button'
                  size='large'
                  variant='text'
                  onClick={() => {
                    navigate(`/${pagesEnum.SITES}`);
                  }}
                >
                  cancel
                </Button>
                <Button
                  color='secondary'
                  variant='contained'
                  data-testid='save-button'
                  disabled={!methods.formState.isValid || isSubmitDisabled}
                  type='submit'
                >
                  save
                </Button>
              </Stack>
            </StyledCardContent>
          </StyledCard>
        </form>
      </FormProvider>
    </Box>
  );
};

export default NewSite;
